import { z } from "zod";
import { FileContentSchema, FileEntrySchema, OkDataSchema } from "./common.js";
import { AgentIdSchema, RelativePathSchema } from "./primitives.js";

/**
 * Workspace + path-shaped file shapes.
 *
 * The path-based memory shapes (read/write/rename/delete/mkdir/versions/
 * pin/lock) were retired in #205 — the flat handle store replaces them via
 * `rpc/memory.ts`. `FileReadPayloadSchema`, `DirectoryListPayloadSchema`,
 * and `FileRenamePayloadSchema` survive because workspace routes still
 * use them.
 */

const WorkspaceEntrySchema = FileEntrySchema.extend({
  contentType: z.string().optional(),
  openableInPortal: z.boolean(),
}).strict();

export const DirectoryListPayloadSchema = z
  .object({ agentId: AgentIdSchema, path: RelativePathSchema.optional() })
  .strict();
export const FileReadPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    path: RelativePathSchema,
    revision: z.number().int().positive().optional(),
  })
  .strict();
export const FileRenamePayloadSchema = z
  .object({ agentId: AgentIdSchema, path: RelativePathSchema, newPath: RelativePathSchema })
  .strict();
export const FilePathPayloadSchema = z
  .object({ agentId: AgentIdSchema, path: RelativePathSchema })
  .strict();

export const WorkspaceListPayloadSchema = DirectoryListPayloadSchema;
export const WorkspaceListDataSchema = z
  .object({ entries: z.array(WorkspaceEntrySchema) })
  .strict();
export const WorkspaceReadPayloadSchema = FilePathPayloadSchema;
export const WorkspaceReadDataSchema = z.object({ file: FileContentSchema }).strict();
export const WorkspaceWritePayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    path: RelativePathSchema,
    filename: z.string().min(1).optional(),
    contentType: z.string().min(1),
    content: z.string().optional(),
    encoding: z.enum(["utf8", "base64"]).optional(),
    objectKey: z.string().min(1).optional(),
    overwrite: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const hasInlineContent = value.content !== undefined;
    const hasObjectKey = value.objectKey !== undefined;
    if (hasInlineContent === hasObjectKey) {
      ctx.addIssue({
        code: "custom",
        message: "workspace write requires exactly one of content or objectKey",
      });
    }
    if (hasObjectKey && value.encoding !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["encoding"],
        message: "encoding is only valid for inline content",
      });
    }
  });
export const WorkspaceWriteDataSchema = OkDataSchema;
export const WorkspaceDeletePayloadSchema = FilePathPayloadSchema;
export const WorkspaceDeleteDataSchema = OkDataSchema;
export const WorkspaceMkdirPayloadSchema = FilePathPayloadSchema;
export const WorkspaceMkdirDataSchema = OkDataSchema;
export const WorkspaceSharePayloadSchema = z.object({ token: z.string().min(1) }).strict();
export const WorkspaceShareDataSchema = z.object({ file: FileContentSchema }).strict();

export type WorkspaceEntry = z.infer<typeof WorkspaceEntrySchema>;
